import { useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./TravelerDetail.module.css";
import AddTravelerModal from "../../../../components/Customers/AddTravelerModal";
import {
  useTravelerById,
  useUpdateTraveler,
  useAddTravelerNote,
} from "../../../../api/people/usePeopleApi";

// ── Helpers ────────────────────────────────────────────────────────────────
const calculateAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const getTravelerType = (age) => {
  if (age === null) return { label: "Unknown", color: "#9ca3af", bg: "#f9fafb", border: "#e5e7eb", icon: "❓" };
  if (age <= 1)     return { label: "Infant",  color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", icon: "👶" };
  if (age <= 11)    return { label: "Child",   color: "#d97706", bg: "#fff7ed", border: "#fed7aa", icon: "👦" };
  return              { label: "Adult",   color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", icon: "🧑" };
};

const formatDob = (dob) => {
  if (!dob) return "—";
  const d = new Date(dob);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const avatarColor = (id) => {
  const colors = ["#f5a51e","#059669","#2563eb","#9333ea","#dc2626","#0891b2","#d97706","#16a34a"];
  return colors[(id ?? 0) % colors.length];
};

// ── API response → UI format ───────────────────────────────────────────────
const mapApiToUi = (data) => {
  if (!data) return null;
  const bookings = Array.isArray(data.bookings) ? data.bookings : [];
  const notes = (Array.isArray(data.notes) ? data.notes : []).map((n) => ({
    id: n.id,
    author: n.author ?? "Staff",
    date: n.createdAt
      ? new Date(n.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "",
    text: n.content ?? n.text,
  }));
  return {
    id: data.id,
    ref: data.travellerCode ?? `TRVL-${String(data.id).padStart(5, "0")}`,
    title: data.title,
    firstName: data.firstName ?? "",
    lastName: data.lastName ?? "",
    dob: data.dateOfBirth ?? data.dob,
    gender: data.gender ?? "",
    nationality: data.nationality ?? "",
    bookings,
    notes,
    createdAt: data.createdAt
      ? new Date(data.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "",
  };
};

// ── Constants ──────────────────────────────────────────────────────────────
const TYPE_STYLES = {
  Package:  { bg: "#eff6ff", color: "#2563eb" },
  Flight:   { bg: "#f0fdf4", color: "#16a34a" },
  Hotel:    { bg: "#fdf4ff", color: "#9333ea" },
  Transfer: { bg: "#ecfeff", color: "#0891b2" },
};
const TYPE_ICON = { Package: "📦", Flight: "✈️", Hotel: "🏨", Transfer: "🚌" };

// ── Sub-components ─────────────────────────────────────────────────────────
const InfoRow = ({ label, value, mono, accent }) => (
  <div className={styles.infoRow}>
    <span className={styles.infoLabel}>{label}</span>
    <span className={`${styles.infoValue} ${mono ? styles.infoMono : ""} ${accent ? styles.infoAccent : ""}`}>
      {value || "—"}
    </span>
  </div>
);

const StatCard = ({ label, value, sub, accent }) => (
  <div className={`${styles.statCard} ${accent ? styles.statCardAccent : ""}`}>
    <div className={styles.statValue}>{value}</div>
    <div className={styles.statLabel}>{label}</div>
    {sub && <div className={styles.statSub}>{sub}</div>}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────
const TravelerDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: apiData, loading, error, refetch } = useTravelerById(id);
  const { execute: updateTraveler, loading: saving, error: updateError } = useUpdateTraveler();
  const { execute: addNote, loading: addingNote } = useAddTravelerNote();

  const t = mapApiToUi(apiData);

  const [activeTab,     setActiveTab]     = useState("overview");
  const [noteText,      setNoteText]      = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);

  const age  = t ? calculateAge(t.dob) : null;
  const type = getTravelerType(age);

  const handleEditSave = useCallback(
    async (payload) => {
      if (!id) return;
      try {
        await updateTraveler(id, {
          title: payload.title,
          firstName: payload.firstName,
          lastName: payload.lastName,
          dateOfBirth: payload.dob,
          gender: payload.gender,
          nationality: payload.nationality,
        });
        setEditModalOpen(false);
        refetch();
      } catch (err) {
        console.error("Update traveler failed:", err);
      }
    },
    [id, updateTraveler, refetch]
  );

  const handleAddNote = useCallback(
    async () => {
      if (!id || !noteText.trim()) return;
      try {
        await addNote(id, { content: noteText.trim(), type: "GENERAL" });
        setNoteText("");
        refetch();
      } catch (err) {
        console.error("Add note failed:", err);
      }
    },
    [id, noteText, addNote, refetch]
  );

  const initials = t ? `${(t.firstName ?? "")[0]}${(t.lastName ?? "")[0]}`.toUpperCase() : "??";

  const TABS = [
    { key: "overview",  label: "Overview" },
    { key: "bookings",  label: "Bookings", count: t?.bookings?.length ?? 0 },
    { key: "notes",     label: "Notes",    count: t?.notes?.length    ?? 0 },
  ];

  if (loading && !t) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}>⏳</div>
          <p className={styles.loadingText}>Loading traveler...</p>
        </div>
      </div>
    );
  }

  if (error && !t) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p className={styles.errorText}>{error}</p>
          <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
          <button className={styles.backBtn} onClick={() => navigate("/people/travelers/profiles")}>
            ← Back to Profiles
          </button>
        </div>
      </div>
    );
  }

  if (!t) return null;

  return (
    <div className={styles.container}>

      {/* ── Error Banner (for update errors) ── */}
      {updateError && (
        <div className={styles.errorBanner}>
          {updateError}
          <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {/* ── Breadcrumb ── */}
      <div className={styles.breadcrumb}>
        <button className={styles.backBtn} onClick={() => navigate("/people/travelers/profiles")}>
          ← Traveler Profiles
        </button>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbCurrent}>{t.firstName} {t.lastName}</span>
      </div>

      {/* ── Profile Header ── */}
      <div className={styles.profileHeader}>
        <div className={styles.profileLeft}>
          <div className={styles.avatarLarge} style={{ background: avatarColor(t.id) }}>
            {initials}
          </div>
          <div className={styles.profileInfo}>
            <div className={styles.profileNameRow}>
              <span className={styles.profileName }>{t.title} {t.firstName} {t.lastName}</span>
              <span
                className={styles.typeBadge}
                style={{ background: type.bg, color: type.color, border: `1px solid ${type.border}` }}
              >
                {type.icon} {type.label}
                {age !== null && <span className={styles.typeAge}> · {age} yrs</span>}
              </span>
            </div>
            <div className={styles.profileRef}>{t.ref}</div>
            <div className={styles.profileMeta}>
              <span>🎂 {formatDob(t.dob)}</span>
              <span className={styles.metaDot}>·</span>
              <span>🌍 {t.nationality}</span>
              <span className={styles.metaDot}>·</span>
              <span>{t.gender || "—"}</span>
              <span className={styles.metaDot}>·</span>
              <span>Since {t.createdAt}</span>
            </div>
          </div>
        </div>
        <div className={styles.profileActions}>
          <button className={styles.editBtn} onClick={() => setEditModalOpen(true)} disabled={saving}>
            {saving ? "Saving..." : "✏️ Edit"}
          </button>
        </div>
      </div>

      {/* ── Quick Stats ── */}
      <div className={styles.statsRow}>
        <StatCard label="Total Bookings" value={t.bookings.length}  sub="All time" />
        <StatCard label="Nationality"    value={t.nationality}       sub="Country of travel doc" />
      </div>

      {/* ── Business Rule Banner ── */}
      <div className={styles.ruleBanner}>
        <span>ℹ️</span>
        <span>
          <strong>Traveler ≠ Customer.</strong> This profile holds travel identity data only.
          Age type (ADT/CHD/INF) is always calculated from date of birth.
        </span>
      </div>

      {/* ── Tab Bar ── */}
      <div className={styles.tabBar}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={`${styles.tabBadge} ${activeTab === tab.key ? styles.tabBadgeActive : ""}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Tab: Overview                                                   */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeTab === "overview" && (
        <div className={styles.tabContent}>
          <div className={styles.twoCol}>

            {/* Identity Card */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}>👤</span>
                <h3>Personal Identity</h3>
                <button className={styles.cardEditBtn} onClick={() => setEditModalOpen(true)}>Edit</button>
              </div>
              <InfoRow label="Title"        value={t.title} />
              <InfoRow label="First Name"   value={t.firstName} />
              <InfoRow label="Last Name"    value={t.lastName} />
              <InfoRow label="Date of Birth" value={formatDob(t.dob)} />
              <InfoRow label="Age"          value={age !== null ? `${age} years old` : "—"} />
              <InfoRow label="Gender"       value={t.gender} />
              <InfoRow label="Nationality"  value={t.nationality} />

              {/* Live type display */}
              <div className={styles.typeDisplayRow}>
                <span className={styles.typeDisplayLabel}>Classified As</span>
                <span
                  className={styles.typeDisplayBadge}
                  style={{ background: type.bg, color: type.color, border: `1px solid ${type.border}` }}
                >
                  {type.icon} {type.label}
                </span>
                <span className={styles.typeDisplayNote}>⚡ Auto-calculated</span>
              </div>
            </div>

          </div>

          {/* Recent Bookings Preview */}
          <div className={styles.card} style={{ marginTop: 14 }}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>🗂️</span>
              <h3>Recent Bookings</h3>
              <button className={styles.cardLinkBtn} onClick={() => setActiveTab("bookings")}>View all →</button>
            </div>
            {t.bookings.length === 0 ? (
              <div className={styles.emptyMini}>No bookings found for this traveler.</div>
            ) : (
              <table className={styles.miniTable}>
                <thead>
                  <tr>
                    <th>Ref</th><th>Type</th><th>Service Dates</th>
                    <th>Price</th><th>Payment</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {t.bookings.slice(0, 3).map(b => {
                    const ts = TYPE_STYLES[b.type] || {};
                    return (
                      <tr key={b.ref}>
                        <td><span className={styles.miniRef}>{b.ref}</span></td>
                        <td>
                          <span className={styles.miniTypeBadge} style={{ background: ts.bg, color: ts.color }}>
                            {TYPE_ICON[b.type]} {b.type}
                          </span>
                        </td>
                        <td>
                          <span className={styles.miniDate}>{b.firstService}</span>
                          {b.lastService !== b.firstService && (
                            <span className={styles.miniDateSub}> → {b.lastService}</span>
                          )}
                        </td>
                        <td><strong>€{b.totalPrice.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</strong></td>
                        <td>
                          <span className={`${styles.miniPayBadge} ${
                            b.paymentStatus === "Paid"    ? styles.payPaid    :
                            b.paymentStatus === "Partial" ? styles.payPartial : styles.payUnpaid}`}>
                            {b.paymentStatus}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles.miniStatusBadge} ${
                            b.bookingStatus === "Confirmed" ? styles.statusConfirmed :
                            b.bookingStatus === "Pending"   ? styles.statusPending   : styles.statusCancelled}`}>
                            {b.bookingStatus}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Tab: Bookings                                                   */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeTab === "bookings" && (
        <div className={styles.tabContent}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>🗂️</span>
              <h3>All Bookings</h3>
              <span className={styles.cardCount}>{t.bookings.length} total</span>
            </div>
            {t.bookings.length === 0 ? (
              <div className={styles.emptyMini}>No bookings for this traveler yet.</div>
            ) : (
              <table className={styles.miniTable}>
                <thead>
                  <tr>
                    <th>Ref</th><th>Type</th><th>First Service</th>
                    <th>Last Service</th><th>Price</th><th>Payment</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {t.bookings.map(b => {
                    const ts = TYPE_STYLES[b.type] || {};
                    return (
                      <tr key={b.ref}>
                        <td><span className={styles.miniRef}>{b.ref}</span></td>
                        <td>
                          <span className={styles.miniTypeBadge} style={{ background: ts.bg, color: ts.color }}>
                            {TYPE_ICON[b.type]} {b.type}
                          </span>
                        </td>
                        <td className={styles.miniDate}>{b.firstService}</td>
                        <td className={styles.miniDate}>{b.lastService}</td>
                        <td><strong>€{b.totalPrice.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</strong></td>
                        <td>
                          <span className={`${styles.miniPayBadge} ${
                            b.paymentStatus === "Paid"    ? styles.payPaid    :
                            b.paymentStatus === "Partial" ? styles.payPartial : styles.payUnpaid}`}>
                            {b.paymentStatus}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles.miniStatusBadge} ${
                            b.bookingStatus === "Confirmed" ? styles.statusConfirmed :
                            b.bookingStatus === "Pending"   ? styles.statusPending   : styles.statusCancelled}`}>
                            {b.bookingStatus}
                          </span>
                        </td>
                        <td>
                          <button
                            className={styles.openBookingBtn}
                            onClick={() => navigate(`/bookings/${b.ref}`, { state: { booking: b, fromTraveler: true } })}
                          >
                            Open →
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Tab: Notes                                                      */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeTab === "notes" && (
        <div className={styles.tabContent}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>📝</span>
              <h3>Internal Notes</h3>
              <span className={styles.cardCount}>{t.notes.length} note{t.notes.length !== 1 ? "s" : ""}</span>
            </div>
            <div className={styles.addNoteBox}>
              <textarea
                className={styles.noteInput}
                placeholder="Add an internal note about this traveler (dietary needs, preferences, special requirements...)"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={3}
              />
              <div className={styles.addNoteFooter}>
                <span className={styles.noteHint}>Visible only to staff</span>
                <button
                  className={styles.addNoteBtn}
                  disabled={!noteText.trim() || addingNote}
                  onClick={handleAddNote}
                >
                  {addingNote ? "Adding..." : "Add Note"}
                </button>
              </div>
            </div>
            <div className={styles.notesList}>
              {t.notes.map(n => (
                <div key={n.id} className={styles.noteItem}>
                  <div className={styles.noteHeader}>
                    <div className={styles.noteAuthorBadge}>{n.author[0]}</div>
                    <div>
                      <span className={styles.noteAuthor}>{n.author}</span>
                      <span className={styles.noteDate}>{n.date}</span>
                    </div>
                  </div>
                  <p className={styles.noteText}>{n.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <AddTravelerModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleEditSave}
        traveler={t}
      />

    </div>
  );
};

export default TravelerDetail;